// src/app/providers.tsx
"use client";

import { ClerkProvider } from "@clerk/nextjs";
import { jaJP } from "@clerk/localizations";
import { useUser } from "@clerk/nextjs";           // ★ 追加
import { usePathname } from "next/navigation";     // ★ 追加
import { RecordProvider, useRecordContext } from "@/contexts/RecordContext";
import { AuthProvider } from "@/contexts/AuthContext";

// --- 修正後（外部URLが来てもアプリ内に強制）---
const rawSignInUrl = process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL || "/sign-in";
const rawSignUpUrl = process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL || "/sign-up";
const rawAfterIn   = process.env.NEXT_PUBLIC_CLERK_AFTER_SIGN_IN_URL || "/";
const rawAfterUp   = process.env.NEXT_PUBLIC_CLERK_AFTER_SIGN_UP_URL || "/";

// http/https で始まる（= Hosted 直リンクなど）はすべてアプリ内に矯正
const signInUrl = rawSignInUrl.startsWith("http") ? "/sign-in" : rawSignInUrl;
const signUpUrl = rawSignUpUrl.startsWith("http") ? "/sign-up" : rawSignUpUrl;
// after のほうは外部に飛ばしたくないなら同様に矯正（任意）
const afterIn   = rawAfterIn.startsWith("http") ? "/" : rawAfterIn;
const afterUp   = rawAfterUp.startsWith("http") ? "/" : rawAfterUp;

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ClerkProvider
  publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}
  localization={{
    ...jaJP,

    // ログイン画面
    signIn: {
      ...jaJP.signIn,
      start: {
        ...jaJP.signIn?.start,
        title: "ログイン",
        actionText: "アカウントをお持ちでない方は",
        actionLink: "新規登録",
      },
    },

    // 新規登録画面
    signUp: {
      ...jaJP.signUp,
      start: {
        ...jaJP.signUp?.start,
        title: "新規登録",
        actionText: "すでにアカウントをお持ちの方は",
        actionLink: "ログイン",
      },
    },

    // ついでに
    userButton: {
      ...jaJP.userButton,
      action__signOut: "ログアウト",
    },
  }}
  signInUrl={signInUrl}
  signUpUrl={signUpUrl}
  signInFallbackRedirectUrl={afterIn}
  signUpFallbackRedirectUrl={afterUp}
>
      <AuthProvider>
        <RecordProvider>
          <GlobalLoadingGate>{children}</GlobalLoadingGate>
        </RecordProvider>
      </AuthProvider>
    </ClerkProvider>
  );
}

function GlobalLoadingGate({ children }: { children: React.ReactNode }) {
  const recordContext = useRecordContext();
  const { isLoaded, isSignedIn } = useUser();
  const path = usePathname();

  // ★ 追加: サインイン/サインアップ/SSO など認証ページの判定をまとめる
  const isAuthPage =
    path.startsWith("/sign-in") ||
    path.startsWith("/sign-up") ||
    path.startsWith("/sign-out") ||
    path.startsWith("/auth/callback") ||
    path.includes("sso-callback"); // Clerkの構成に合わせて必要なら調整

  if (!recordContext) {
    return (
      <div className="fixed inset-0 bg-white/80 flex flex-col items-center justify-center z-[9999]">
        <div className="text-red-600 font-bold text-xl">Context未提供エラー</div>
        <div className="text-gray-600 mt-2">システムエラー。リロードしても直らない場合は管理者へご連絡ください。</div>
      </div>
    );
  }

  const userProfileLoading = recordContext.userProfileLoading;

  // ★ サインイン系ルートではオーバーレイを出さない
  const showOverlay =
    Boolean(userProfileLoading) && isLoaded && isSignedIn && !isAuthPage;

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 bg-white/70 flex flex-col items-center justify-center z-[9999]">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-400 border-t-transparent mb-4" />
          <div className="text-blue-700 font-semibold text-lg">ロード中...</div>
        </div>
      )}
      <main className={showOverlay ? "pointer-events-none select-none blur-sm" : ""}>
        {children}
      </main>
    </>
  );
}
